import { BasePagingResponse, BaseResponse } from 'models/_base';
import { AirbnbCancellationPolicy } from 'models/AirbnbCancellationPolicy';
import { VrboCancellationPolicy } from 'models/Channels';
import { AppliesToMetadata } from 'models/Properties';

export enum SeasonalRuleColor {
  BLUE = 'BLUE',
  GREEN = 'GREEN',
  PINK = 'PINK',
  PURPLE = 'PURPLE',
  YELLOW = 'YELLOW',
}

export interface GetSeasonalRulesResponse
  extends BaseResponse,
    BasePagingResponse {
  items: SeasonalRule[];
}

export enum SeasonalRulePolicyChannel {
  AIRBNB = 'AIRBNB',
  BOOKINGDOTCOM = 'BCOM',
  HOSTFULLY = 'HOSTFULLY',
  HVMB = 'HVMB',
  VRBO = 'VRBO',
}

export type CancellationPolicySummaryEntry =
  | {
      channel: SeasonalRulePolicyChannel.HOSTFULLY;
      displayLabel: string;
    }
  | {
      channel: Exclude<
        SeasonalRulePolicyChannel,
        SeasonalRulePolicyChannel.HOSTFULLY
      >;
      policyCode: string;
      displayLabel: string;
    };

export interface SeasonalRule extends AppliesToMetadata {
  uid: string;
  name: string;
  color: SeasonalRuleColor;
  startDate: string;
  endDate: string;
  cancellationPolicySummary: CancellationPolicySummaryEntry[];
  applyToAllProperties: boolean;
  propertyCount: number;
}

export enum SeasonalRuleHvmbPolicyCode {
  CANCELLATION_14_DAYS_WITH_GRACE_PERIOD = '_14_DAYS_WITH_GRACE_PERIOD',
  CANCELLATION_30_DAYS_WITH_GRACE_PERIOD = '_30_DAYS_WITH_GRACE_PERIOD',
  CANCELLATION_60_DAYS_WITH_GRACE_PERIOD = '_60_DAYS_WITH_GRACE_PERIOD',
  CANCELLATION_90_DAYS_WITH_GRACE_PERIOD = '_90_DAYS_WITH_GRACE_PERIOD',
}

export type VrboStandardPolicyCode = Exclude<
  VrboCancellationPolicy,
  VrboCancellationPolicy.CUSTOM
>;

export interface VrboCustomTier {
  refundPercentage: number;
  refundThreshold: number;
  tierOrder: number;
}

export interface HostfullyPolicyEntry {
  channel: SeasonalRulePolicyChannel.HOSTFULLY;
  policyText: string;
  displayLabel: string;
}

export interface AirbnbPolicyEntry {
  channel: SeasonalRulePolicyChannel.AIRBNB;
  policyCode: AirbnbCancellationPolicy;
  displayLabel: string;
}

export interface VrboStandardPolicyEntry {
  channel: SeasonalRulePolicyChannel.VRBO;
  policyCode: VrboStandardPolicyCode;
  displayLabel: string;
}

export interface VrboCustomPolicyEntry {
  channel: SeasonalRulePolicyChannel.VRBO;
  policyCode: VrboCancellationPolicy.CUSTOM;
  customTiers: VrboCustomTier[];
  displayLabel: string;
}

export interface BookingDotComPolicyEntry {
  channel: SeasonalRulePolicyChannel.BOOKINGDOTCOM;
  policyCode: string;
  displayLabel: string;
}

export interface HvmbPolicyEntry {
  channel: SeasonalRulePolicyChannel.HVMB;
  policyCode: SeasonalRuleHvmbPolicyCode;
  displayLabel: string;
}

export type CancellationPolicyEntry =
  | HostfullyPolicyEntry
  | AirbnbPolicyEntry
  | VrboStandardPolicyEntry
  | VrboCustomPolicyEntry
  | BookingDotComPolicyEntry
  | HvmbPolicyEntry;

export type PayloadCancellationPolicyEntry =
  | Omit<HostfullyPolicyEntry, 'displayLabel'>
  | Omit<AirbnbPolicyEntry, 'displayLabel'>
  | Omit<VrboStandardPolicyEntry, 'displayLabel'>
  | Omit<VrboCustomPolicyEntry, 'displayLabel'>
  | Omit<BookingDotComPolicyEntry, 'displayLabel'>
  | Omit<HvmbPolicyEntry, 'displayLabel'>;

export interface SeasonalRulePayload {
  name: string;
  color: SeasonalRuleColor;
  startDate: string;
  endDate: string;
  applyToAllProperties: boolean;
  propertyUids?: string[];
  cancellationPolicies: PayloadCancellationPolicyEntry[];
}

// Channels that support automated sync (Hostfully has no channel push).
export type SyncStatusChannel = Exclude<
  SeasonalRulePolicyChannel,
  SeasonalRulePolicyChannel.HOSTFULLY
>;

export interface SeasonalRulePropertyError {
  propertyUid: string;
  propertyName: string;
  errorCode: string;
  message: string;
}

export interface SeasonalRuleDetail extends SeasonalRule {
  properties: { uid: string; name: string }[];
  cancellationPolicies: CancellationPolicyEntry[];
  propertyErrors: SeasonalRulePropertyError[];
  syncStatus?: Partial<
    Record<
      SyncStatusChannel,
      { synced: number; pending: number; failed: number }
    >
  >;
  createdAt?: string;
  updatedAt?: string;
}

export interface DeleteSeasonalRulePayload {
  applyToAll: boolean;
  propertyUids?: string[];
}
